import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { GALLERY_CONFIG } from '../../config/gallery';
import { 
  fetchImagesFromFolder, 
  getImageUrl, 
  getAlternativeImageUrls,
  getVideoUrl
} from '../../services/googleDrive';
import {
  GallerySection,
  Container,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  GalleryContainer,
  ImageWrapper,
  GalleryImage,
  VideoIframe,
  ImagePlaceholder,
  LoadingMessage,
  ErrorMessage,
  LightboxOverlay,
  LightboxContent,
  LightboxImage,
  LightboxVideo,
  CloseButton,
  LightboxPrev,
  LightboxNext,
  LightboxCounter
} from './Gallery.styles.jsx';

const ImageSlide = ({ fileId, index, onSlideInteraction, onExpand }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [imageSrc, setImageSrc] = useState('');
  const preloadImgRef = useRef(null);

  useEffect(() => {
    if (fileId) {
      const alternativeUrls = getAlternativeImageUrls(fileId);
      const primaryUrl = alternativeUrls[0] || getImageUrl(fileId);
      setImageSrc(primaryUrl);
      
      // Preload image in background to reduce visible loading time
      if (primaryUrl && preloadImgRef.current === null) {
        const preloadImg = new Image();
        preloadImgRef.current = preloadImg;
        preloadImg.src = primaryUrl;
        preloadImg.onload = () => {
          // Image is ready, this helps browser cache
        };
        preloadImg.onerror = () => {
          // If primary fails, try alternatives
          if (alternativeUrls.length > 1) {
            const fallbackImg = new Image();
            fallbackImg.src = alternativeUrls[1];
          }
        };
      }
    }
  }, [fileId]);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    const alternativeUrls = getAlternativeImageUrls(fileId);
    const nextIndex = currentUrlIndex + 1;
    
    if (nextIndex < alternativeUrls.length) {
      // Try next alternative URL
      setCurrentUrlIndex(nextIndex);
      setImageSrc(alternativeUrls[nextIndex]);
      setLoading(true); // Reset loading state for next attempt
      setError(false);
    } else {
      // All URLs failed
      setLoading(false);
      setError(true);
      console.error(`Failed to load image ${fileId} after trying ${alternativeUrls.length} URLs`);
    }
  };

  const handleClick = (e) => {
    // Stop event propagation to prevent gallery rotation
    e.stopPropagation();
    if (onSlideInteraction) {
      onSlideInteraction();
    }
    // Open lightbox
    if (onExpand) {
      onExpand(index);
    }
  };

  return (
    <ImageWrapper onClick={handleClick}>
      {loading && !error && (
        <ImagePlaceholder>Se încarcă...</ImagePlaceholder>
      )}
      {error && (
        <ImagePlaceholder style={{ color: '#ef4444' }}>
          Eroare la încărcare
        </ImagePlaceholder>
      )}
      {imageSrc && (
        <GalleryImage
          key={`${fileId}-${currentUrlIndex}`}
          src={imageSrc}
          alt={`Lucrare ${index + 1}`}
          loading="eager"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ display: loading || error ? 'none' : 'block', cursor: 'pointer' }}
        />
      )}
    </ImageWrapper>
  );
};

const VideoSlide = ({ fileId, index, onVideoInteraction, onExpand }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (fileId) {
      const videoUrl = getVideoUrl(fileId);
      setVideoSrc(videoUrl);
      // Videos load differently - set loading to false after a short delay
      setTimeout(() => setLoading(false), 1000);
    }
  }, [fileId]);

  useEffect(() => {
    // Listen for interaction events on the iframe
    const iframe = iframeRef.current;
    if (!iframe || loading) return;

    const handleInteraction = () => {
      if (onVideoInteraction) {
        onVideoInteraction();
      }
    };

    // Listen for clicks on the iframe wrapper (when user clicks to play)
    const wrapper = iframe.parentElement;
    if (wrapper) {
      // Add click handler to wrapper
      wrapper.addEventListener('click', handleInteraction, true); // Use capture phase
      wrapper.addEventListener('touchstart', handleInteraction, true);
      wrapper.addEventListener('mousedown', handleInteraction, true);
    }

    // Try to detect when iframe receives focus (user clicked on video)
    iframe.addEventListener('focus', handleInteraction, true);
    iframe.addEventListener('click', handleInteraction, true);

    // Also listen for pointer events (more reliable for touch)
    const container = wrapper?.closest('.slick-slide');
    if (container) {
      container.addEventListener('pointerdown', handleInteraction, true);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('click', handleInteraction, true);
        wrapper.removeEventListener('touchstart', handleInteraction, true);
        wrapper.removeEventListener('mousedown', handleInteraction, true);
      }
      if (iframe) {
        iframe.removeEventListener('focus', handleInteraction, true);
        iframe.removeEventListener('click', handleInteraction, true);
      }
      if (container) {
        container.removeEventListener('pointerdown', handleInteraction, true);
      }
    };
  }, [onVideoInteraction, loading]);

  const handleClick = (e) => {
    // Open lightbox on click
    if (onExpand) {
      onExpand(index);
    }
  };

  return (
    <ImageWrapper onClick={handleClick} style={{ cursor: 'pointer' }}>
      {loading && !error && (
        <ImagePlaceholder>Se încarcă video...</ImagePlaceholder>
      )}
      {error && (
        <ImagePlaceholder style={{ color: '#ef4444' }}>
          Eroare la încărcare video
        </ImagePlaceholder>
      )}
      {videoSrc && !error && (
        <VideoIframe
          ref={iframeRef}
          src={videoSrc}
          title={`Video ${index + 1}`}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          style={{
            display: loading ? 'none' : 'block',
            pointerEvents: 'auto'
          }}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
        />
      )}
    </ImageWrapper>
  );
};

const Gallery = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const loadMedia = async () => {
      try {
        setLoading(true);
        setError(null);

        if (GALLERY_CONFIG.folderId) {
          const files = await fetchImagesFromFolder(GALLERY_CONFIG.folderId);
          if (files.length > 0) {
            setMediaFiles(files);
            // Preload first few images immediately
            files.slice(0, 3).forEach(file => {
              const fileId = typeof file === 'string' ? file : file.id;
              const fileType = typeof file === 'object' ? file.type : 'image';
              if (fileType === 'image') {
                const img = new Image();
                img.src = getImageUrl(fileId);
              }
            });
          } else {
            setError('Nu s-au găsit imagini sau videouri în folder.');
          }
        } else {
          setError('Nu este configurat niciun folder sau imagini.');
        }
      } catch (err) {
        console.error('Error loading gallery media:', err);
        setError('Eroare la încărcarea galeriei.');
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
  }, []);

  // Update slider autoplay when autoplayEnabled changes
  useEffect(() => {
    if (sliderRef.current) {
      if (autoplayEnabled) {
        sliderRef.current.slickPlay();
      } else {
        sliderRef.current.slickPause();
      }
    }
  }, [autoplayEnabled]);

  // Preload upcoming slides to prevent loading delays
  useEffect(() => {
    if (mediaFiles.length === 0) return;

    // Preload next 2-3 slides ahead
    const preloadCount = 3;
    for (let i = 1; i <= preloadCount; i++) {
      const nextIndex = (currentSlide + i) % mediaFiles.length;
      const prevIndex = (currentSlide - i + mediaFiles.length) % mediaFiles.length;
      
      const nextFile = mediaFiles[nextIndex];
      const prevFile = mediaFiles[prevIndex];

      if (nextFile) {
        const fileId = typeof nextFile === 'string' ? nextFile : nextFile.id;
        const fileType = typeof nextFile === 'object' ? nextFile.type : 'image';
        
        if (fileType === 'image') {
          // Preload image by creating an Image object
          const img = new Image();
          const imageUrl = getImageUrl(fileId);
          img.src = imageUrl;
        }
      }

      if (prevFile) {
        const fileId = typeof prevFile === 'string' ? prevFile : prevFile.id;
        const fileType = typeof prevFile === 'object' ? prevFile.type : 'image';
        
        if (fileType === 'image') {
          // Preload image
          const img = new Image();
          const imageUrl = getImageUrl(fileId);
          img.src = imageUrl;
        }
      }
    }
  }, [currentSlide, mediaFiles]);

  const handleUserInteraction = () => {
    if (autoplayEnabled) {
      setAutoplayEnabled(false);
      // Stop the slider immediately
      if (sliderRef.current && sliderRef.current.slickPause) {
        sliderRef.current.slickPause();
      }
    }
  };

  const handleVideoInteraction = () => {
    handleUserInteraction();
  };

  const handleSlideInteraction = () => {
    handleUserInteraction();
  };

  const handleExpand = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    // Stop autoplay when opening lightbox
    handleUserInteraction();
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handleNext = () => {
    if (lightboxIndex < mediaFiles.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  const handlePrev = () => {
    if (lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setLightboxOpen(false);
          break;
        case 'ArrowRight':
          if (lightboxIndex < mediaFiles.length - 1) {
            setLightboxIndex(lightboxIndex + 1);
          }
          break;
        case 'ArrowLeft':
          if (lightboxIndex > 0) {
            setLightboxIndex(lightboxIndex - 1);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, lightboxIndex, mediaFiles.length]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen]);

  const settings = {
    dots: true,
    infinite: mediaFiles.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, mediaFiles.length),
    slidesToScroll: 1,
    lazyLoad: 'progressive', // Changed to 'progressive' to preload adjacent slides
    autoplay: autoplayEnabled && mediaFiles.length > 1,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    // Stop autoplay when user interacts
    onSwipe: handleUserInteraction,
    onTouchMove: handleUserInteraction,
    onDotsClick: handleUserInteraction,
    beforeChange: (current, next) => {
      // Update current slide index for preloading
      setCurrentSlide(next);
    },
    afterChange: (index) => {
      // Update current slide index
      setCurrentSlide(index);
      
      // Stop autoplay if current slide is a video (videos need user attention)
      const currentFile = mediaFiles[index];
      if (currentFile) {
        const fileType = typeof currentFile === 'object' ? currentFile.type : 'image';
        if (fileType === 'video') {
          handleUserInteraction();
        }
      }
    },
    // Note: beforeChange fires for both autoplay and manual, so we handle clicks/swipes separately
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(2, mediaFiles.length),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  if (loading) {
    return (
      <GallerySection id="galerie">
        <Container>
          <SectionHeader>
            <SectionTitle>Galerie Foto</SectionTitle>
            <LoadingMessage>Se încarcă galeria...</LoadingMessage>
          </SectionHeader>
        </Container>
      </GallerySection>
    );
  }

  if (error || mediaFiles.length === 0) {
    return (
      <GallerySection id="galerie">
        <Container>
          <SectionHeader>
            <SectionTitle>Galerie Foto și Video</SectionTitle>
            <ErrorMessage>{error || 'Nu sunt imagini sau videouri configurate'}</ErrorMessage>
            <SectionSubtitle>
              Configurează imaginile și videourile în fișierul de configurare
            </SectionSubtitle>
          </SectionHeader>
        </Container>
      </GallerySection>
    );
  }

  return (
    <GallerySection id="galerie">
      <Container>
        <SectionHeader>
          <SectionTitle>Galerie Foto și Video</SectionTitle>
          <SectionSubtitle>
            Vizualizează exemple de lucrări efectuate
          </SectionSubtitle>
        </SectionHeader>
        
        <GalleryContainer>
          <Slider ref={sliderRef} {...settings}>
            {mediaFiles.map((file, index) => {
              const fileId = typeof file === 'string' ? file : file.id;
              const fileType = typeof file === 'object' ? file.type : 'image';
              
              if (fileType === 'video') {
                return (
                  <VideoSlide 
                    key={fileId || index} 
                    fileId={fileId} 
                    index={index}
                    onVideoInteraction={handleVideoInteraction}
                    onExpand={handleExpand}
                  />
                );
              } else {
                return (
                  <ImageSlide 
                    key={fileId || index} 
                    fileId={fileId} 
                    index={index}
                    onSlideInteraction={handleSlideInteraction}
                    onExpand={handleExpand}
                  />
                );
              }
            })}
          </Slider>
        </GalleryContainer>
      </Container>

      {/* Lightbox Modal */}
      <LightboxOverlay 
        isOpen={lightboxOpen} 
        onClick={handleCloseLightbox}
      >
        <LightboxContent 
          isOpen={lightboxOpen}
          onClick={(e) => e.stopPropagation()}
        >
          {lightboxOpen && mediaFiles[lightboxIndex] && (() => {
            const currentFile = mediaFiles[lightboxIndex];
            const fileId = typeof currentFile === 'string' ? currentFile : currentFile.id;
            const fileType = typeof currentFile === 'object' ? currentFile.type : 'image';
            
            return fileType === 'video' ? (
              <LightboxVideo
                src={getVideoUrl(fileId)}
                title={`Video ${lightboxIndex + 1}`}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                isLandscape={true}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <LightboxImage
                src={getImageUrl(fileId)}
                alt={`Lucrare ${lightboxIndex + 1}`}
                onError={(e) => {
                  // Try alternative URL on error
                  const altUrls = getAlternativeImageUrls(fileId);
                  if (altUrls.length > 1) {
                    e.target.src = altUrls[1];
                  }
                }}
              />
            );
          })()}
          
          <CloseButton onClick={handleCloseLightbox} aria-label="Close">
            ×
          </CloseButton>
          
          {mediaFiles.length > 1 && (
            <>
              <LightboxPrev
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                disabled={lightboxIndex === 0}
                aria-label="Previous"
              >
                ‹
              </LightboxPrev>
              <LightboxNext
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                disabled={lightboxIndex === mediaFiles.length - 1}
                aria-label="Next"
              >
                ›
              </LightboxNext>
              <LightboxCounter>
                {lightboxIndex + 1} / {mediaFiles.length}
              </LightboxCounter>
            </>
          )}
        </LightboxContent>
      </LightboxOverlay>
    </GallerySection>
  );
};

export default Gallery;